import { z } from 'zod';
import { SocialNetwork } from '../enums';

export const socialMediaItemSchema = z.object({
  storageKey: z.string().min(1).max(500),
  mimeType: z.string().max(120),
  width: z.number().int().min(1).optional(),
  height: z.number().int().min(1).optional(),
  durationSeconds: z.number().min(0).optional(),
  altText: z.string().max(500).optional(),
  order: z.number().int().min(0).default(0),
});
export type SocialMediaItem = z.infer<typeof socialMediaItemSchema>;

export const SocialPostType = {
  IMAGE: 'IMAGE',
  CAROUSEL: 'CAROUSEL',
  VIDEO: 'VIDEO',
  REEL: 'REEL',
  STORY: 'STORY',
  TEXT: 'TEXT',
} as const;
export type SocialPostType = (typeof SocialPostType)[keyof typeof SocialPostType];

export const createSocialPostSchema = z
  .object({
    clientId: z.string().uuid(),
    socialAccountIds: z.array(z.string().uuid()).min(1).max(5),
    type: z.nativeEnum(SocialPostType),
    caption: z.string().max(2200),
    firstComment: z.string().max(2200).optional(),
    hashtags: z.array(z.string().max(60)).max(30).default([]),
    media: z.array(socialMediaItemSchema).max(10).default([]),
    scheduledFor: z.coerce.date(),
    /** Usar o agendamento nativo do IG além da fila própria. */
    useNativeScheduling: z.boolean().default(false),
    linkUrl: z.string().url().optional(),
    requiresClientApproval: z.boolean().default(false),
  })
  .refine((v) => v.type === 'TEXT' || v.media.length > 0, {
    message: 'Posts de imagem/vídeo exigem pelo menos uma mídia',
    path: ['media'],
  })
  .refine((v) => v.type !== 'CAROUSEL' || v.media.length >= 2, {
    message: 'Carrossel exige no mínimo 2 mídias',
    path: ['media'],
  });
export type CreateSocialPostInput = z.infer<typeof createSocialPostSchema>;

export const updateSocialPostSchema = z.object({
  caption: z.string().max(2200).optional(),
  firstComment: z.string().max(2200).optional(),
  hashtags: z.array(z.string().max(60)).max(30).optional(),
  media: z.array(socialMediaItemSchema).max(10).optional(),
  scheduledFor: z.coerce.date().optional(),
  linkUrl: z.string().url().optional(),
});
export type UpdateSocialPostInput = z.infer<typeof updateSocialPostSchema>;

/** Saída da IA para legenda de post. */
export const captionGenerationSchema = z.object({
  hook: z.string().max(160),
  mainText: z.string().max(1800),
  hashtags: z.array(z.string().max(40)).max(20),
  cta: z.string().max(120),
  firstComment: z.string().max(500),
  variations: z
    .array(
      z.object({
        label: z.string().max(40),
        hook: z.string().max(160),
        mainText: z.string().max(1800),
      }),
    )
    .max(3),
});
export type CaptionGeneration = z.infer<typeof captionGenerationSchema>;

export const generateCaptionSchema = z.object({
  clientId: z.string().uuid(),
  network: z.nativeEnum(SocialNetwork),
  topic: z.string().min(3).max(1000),
  planIds: z.array(z.string().uuid()).max(10).default([]),
  goal: z.enum(['ENGAGEMENT', 'SALES', 'EDUCATION', 'INSTITUTIONAL', 'PROMOTION']),
  includeEmojis: z.boolean().default(true),
  extraInstructions: z.string().max(2000).optional(),
});
export type GenerateCaptionInput = z.infer<typeof generateCaptionSchema>;

/* ------------------------------------------------------------------------- *
 * LIMITES POR REDE — respeitados pelo agendador.
 * ------------------------------------------------------------------------- */

export const SOCIAL_LIMITS = {
  INSTAGRAM: {
    captionMax: 2200,
    hashtagsMax: 30,
    postsPer24h: 25,
    maxPendingContainers: 50,
    carouselMax: 10,
    nativeScheduleMinMinutes: 10,
    nativeScheduleMaxDays: 75,
    supportsNativeScheduling: true,
  },
  FACEBOOK: {
    captionMax: 5000,
    hashtagsMax: 30,
    postsPer24h: 50,
    maxPendingContainers: 50,
    carouselMax: 10,
    nativeScheduleMinMinutes: 10,
    nativeScheduleMaxDays: 75,
    supportsNativeScheduling: true,
  },
  LINKEDIN: {
    captionMax: 3000,
    hashtagsMax: 20,
    postsPer24h: 20,
    maxPendingContainers: 0,
    carouselMax: 9,
    nativeScheduleMinMinutes: 0,
    nativeScheduleMaxDays: 0,
    /** LinkedIn não tem agendamento nativo — a fila própria é obrigatória. */
    supportsNativeScheduling: false,
  },
} as const satisfies Record<SocialNetwork, Record<string, unknown>>;

export const calendarQuerySchema = z.object({
  clientId: z.string().uuid().optional(),
  from: z.coerce.date(),
  to: z.coerce.date(),
  networks: z.array(z.nativeEnum(SocialNetwork)).optional(),
});
export type CalendarQuery = z.infer<typeof calendarQuerySchema>;
